import React, { useState, useRef, useEffect, useCallback } from "react";
import { CheckCircle, XCircle, AlertTriangle, RotateCcw, Camera } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ Shared Scanner Core ============
const useQrScanner = (onResult) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const scanningRef = useRef(true);

  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    scanningRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        scanLoop();
      }
    } catch (err) {
      console.error("Camera access error:", err);
    }
  }, []);

  const scanLoop = useCallback(() => {
    if (!scanningRef.current || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0);

      // Use BarcodeDetector API if available (native, fast)
      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        detector.detect(canvas).then(barcodes => {
          if (barcodes.length > 0 && scanningRef.current) {
            scanningRef.current = false;
            stopCamera();
            onResult(barcodes[0].rawValue);
          }
        }).catch(() => {});
      } else {
        // Fallback: use html5-qrcode's internal decoder
        try {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          // We'll rely on BarcodeDetector or manual input for browsers without it
        } catch (e) {}
      }
    }

    if (scanningRef.current) {
      animFrameRef.current = requestAnimationFrame(scanLoop);
    }
  }, [onResult, stopCamera]);

  return { videoRef, canvasRef, startCamera, stopCamera };
};

const ScannerView = ({ onScan }) => {
  const { videoRef, canvasRef, startCamera, stopCamera } = useQrScanner(onScan);
  const [cameraReady, setCameraReady] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (mounted) setCameraReady(true);
          };
          await videoRef.current.play();
        }

        // Start scanning with BarcodeDetector
        if ("BarcodeDetector" in window) {
          const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
          const scanFrame = async () => {
            if (!mounted || !videoRef.current || videoRef.current.readyState < 2) {
              if (mounted) requestAnimationFrame(scanFrame);
              return;
            }
            try {
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0 && mounted) {
                stream.getTracks().forEach(t => t.stop());
                onScan(barcodes[0].rawValue);
                return;
              }
            } catch (e) {}
            if (mounted) requestAnimationFrame(scanFrame);
          };
          requestAnimationFrame(scanFrame);
        } else {
          // Fallback: use html5-qrcode library
          const { Html5Qrcode } = await import("html5-qrcode");
          // Stop raw stream, use library instead
          stream.getTracks().forEach(t => t.stop());
          if (!mounted) return;

          const qr = new Html5Qrcode("fallback-scanner");
          try {
            await qr.start(
              { facingMode: "environment" },
              { fps: 15, qrbox: { width: 250, height: 250 } },
              (text) => { if (mounted) { qr.stop().catch(() => {}); onScan(text); } },
              () => {}
            );
            if (mounted) setCameraReady(true);
          } catch (e) {
            console.error("Fallback scanner error:", e);
          }

          // Cleanup for fallback
          const origCleanup = () => { qr.stop().catch(() => {}); qr.clear().catch(() => {}); };
          videoRef.current._fallbackCleanup = origCleanup;
        }
      } catch (err) {
        console.error("Camera init error:", err);
        if (mounted) setShowManual(true);
      }
    };

    init();

    return () => {
      mounted = false;
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        videoRef.current.srcObject = null;
      }
      if (videoRef.current?._fallbackCleanup) {
        videoRef.current._fallbackCleanup();
      }
    };
  }, []);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) onScan(manualInput.trim());
  };

  return (
    <div className="space-y-3">
      {/* Camera view */}
      <div className="relative rounded-xl overflow-hidden bg-black" style={{ minHeight: 300 }}>
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="w-full h-full object-cover"
          style={{ minHeight: 300 }}
        />
        <canvas ref={canvasRef} className="hidden" />
        <div id="fallback-scanner" className="absolute inset-0" />

        {/* Scanning frame overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-56 h-56 relative">
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-3 border-l-3 border-white rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-3 border-r-3 border-white rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-3 border-l-3 border-white rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-3 border-r-3 border-white rounded-br-lg" />
              {/* Scanning line animation */}
              <div className="absolute left-2 right-2 h-0.5 bg-primary/80 animate-pulse" style={{ top: "50%" }} />
            </div>
          </div>
          {/* Instruction */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <span className="bg-black/60 text-white text-sm px-4 py-2 rounded-full">
              {cameraReady ? "Visez le QR code" : "Démarrage caméra..."}
            </span>
          </div>
        </div>
      </div>

      {/* Manual input fallback */}
      {showManual && (
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <input
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            placeholder="Entrez le code du billet..."
            className="flex-1 px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
          />
          <Button type="submit" size="sm">Valider</Button>
        </form>
      )}
    </div>
  );
};

// ============ Result Display ============
const ResultView = ({ result, onReset }) => (
  <div className="text-center py-6">
    {result.type === "success" && (
      <div className="space-y-3 animate-in fade-in">
        <div className="w-28 h-28 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
          <CheckCircle className="w-16 h-16 text-green-500" />
        </div>
        <h2 className="text-3xl font-bold text-green-500">VALIDÉ</h2>
        <p className="text-lg text-muted-foreground">{result.detail}</p>
      </div>
    )}
    {result.type === "warning" && (
      <div className="space-y-3 animate-in fade-in">
        <div className="w-28 h-28 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-16 h-16 text-yellow-500" />
        </div>
        <h2 className="text-3xl font-bold text-yellow-500">DÉJÀ SCANNÉ</h2>
        <p className="text-lg text-muted-foreground">{result.detail}</p>
      </div>
    )}
    {result.type === "error" && (
      <div className="space-y-3 animate-in fade-in">
        <div className="w-28 h-28 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
          <XCircle className="w-16 h-16 text-red-500" />
        </div>
        <h2 className="text-3xl font-bold text-red-500">INVALIDE</h2>
        <p className="text-lg text-muted-foreground">{result.message}</p>
      </div>
    )}
    <Button onClick={onReset} className="mt-8 w-full py-7 text-xl" data-testid="scan-again-btn">
      <RotateCcw className="w-6 h-6 mr-2" />
      Scanner suivant
    </Button>
  </div>
);

// ============ Parse QR Code ============
const parseQrCode = (text) => {
  let orderId = null;
  let ticketIndex = 0;

  if (text.startsWith("TIKA-TICKET:")) {
    const parts = text.replace("TIKA-TICKET:", "").split(":");
    orderId = parts[0];
    ticketIndex = parts[1] ? parseInt(parts[1]) : 0;
  } else if (text.startsWith("TIKA-")) {
    orderId = text.replace("TIKA-", "");
  } else {
    try { const d = JSON.parse(text); orderId = d.orderId; ticketIndex = d.ticketIndex || 0; }
    catch { orderId = text; }
  }
  return { orderId, ticketIndex };
};

// ============ Admin Scanner ============
export const TicketScanner = () => {
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(true);

  const handleScan = async (text) => {
    setShowScanner(false);
    const { orderId, ticketIndex } = parseQrCode(text);
    if (!orderId) { setScanResult({ type: "error", message: "QR code invalide" }); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem("tika_admin_token");
      const res = await axios.post(`${API}/admin/validate-ticket`,
        { order_id: orderId, ticket_index: ticketIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      const customerName = d.order?.customer_name || d.customer_name || "";
      const eventTitle = d.order?.items?.[0]?.event_title || d.event_title || "";
      setScanResult(d.already_validated
        ? { type: "warning", message: "Déjà scanné", detail: customerName }
        : { type: "success", message: "Validé", detail: `${customerName} — ${eventTitle}` }
      );
    } catch (err) {
      setScanResult({ type: "error", message: err.response?.data?.detail || "Billet invalide" });
    } finally { setLoading(false); }
  };

  const reset = () => { setScanResult(null); setLoading(false); setShowScanner(true); };

  return (
    <div data-testid="ticket-scanner">
      {showScanner && !loading && !scanResult && <ScannerView onScan={handleScan} />}
      {loading && (
        <div className="text-center py-16">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground text-lg">Vérification du billet...</p>
        </div>
      )}
      {scanResult && <ResultView result={scanResult} onReset={reset} />}
    </div>
  );
};

// ============ Organizer Scanner ============
export const OrganizerTicketScanner = () => {
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(true);

  const handleScan = async (text) => {
    setShowScanner(false);
    const { orderId, ticketIndex } = parseQrCode(text);
    if (!orderId) { setScanResult({ type: "error", message: "QR code invalide" }); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const res = await axios.post(`${API}/organizer/validate-ticket`,
        { order_id: orderId, ticket_index: ticketIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      const customerName = d.order?.customer_name || d.customer_name || "";
      const eventTitle = d.order?.items?.[0]?.event_title || d.event_title || "";
      setScanResult(d.already_validated
        ? { type: "warning", message: "Déjà scanné", detail: customerName }
        : { type: "success", message: "Validé", detail: `${customerName} — ${eventTitle}` }
      );
    } catch (err) {
      setScanResult({ type: "error", message: err.response?.data?.detail || "Billet invalide" });
    } finally { setLoading(false); }
  };

  const reset = () => { setScanResult(null); setLoading(false); setShowScanner(true); };

  return (
    <div data-testid="organizer-ticket-scanner">
      {showScanner && !loading && !scanResult && <ScannerView onScan={handleScan} />}
      {loading && (
        <div className="text-center py-16">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground text-lg">Vérification du billet...</p>
        </div>
      )}
      {scanResult && <ResultView result={scanResult} onReset={reset} />}
    </div>
  );
};
